import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import useFullAudioPlayer from '../hooks/useFullAudioPlayer';
import { DEFAULT_RECITER } from '../services/quranAPI';

const QuranPlayerContext = createContext(null);

export const useQuranPlayer = () => {
  const ctx = useContext(QuranPlayerContext);
  if (!ctx) throw new Error('useQuranPlayer must be used within a QuranPlayerProvider');
  return ctx;
};

/**
 * Holds ONE audio player instance for the whole app so Quran recitation keeps
 * playing (and stays controllable via the mini bar) as the user navigates.
 */
export const QuranPlayerProvider = ({ children }) => {
  const [verses, setVerses] = useState([]);
  const [chapter, setChapter] = useState(null);
  const [reciterId, setReciterId] = useState(DEFAULT_RECITER);
  const [readMode, setReadMode] = useState(() => localStorage.getItem('quran_read_mode') || 'reading');

  const onVerseEndedRef = useRef(null);
  const onSurahEndedRef = useRef(null);

  const player = useFullAudioPlayer({
    verses,
    reciterId,
    chapterName: chapter?.name_simple || '',
    readMode,
    onVerseEnded: () => onVerseEndedRef.current?.(),
    onSurahEnded: () => onSurahEndedRef.current?.(),
  });

  // The reader registers its per-page callbacks; cleared when it unmounts.
  const setCallbacks = useCallback((onVerseEnded, onSurahEnded) => {
    onVerseEndedRef.current = onVerseEnded;
    onSurahEndedRef.current = onSurahEnded;
  }, []);

  return (
    <QuranPlayerContext.Provider value={{
      player,
      verses, setVerses,
      chapter, setChapter,
      reciterId, setReciterId,
      readMode, setReadMode,
      setCallbacks,
    }}>
      {children}
    </QuranPlayerContext.Provider>
  );
};
